"use client";

import { type AriaTextFieldOptions, useTextField } from "@react-aria/textfield";
import { useObjectRef } from "@react-aria/utils";
import type { VariantProps } from "class-variance-authority";
import {
  type ChangeEvent,
  type HTMLInputTypeAttribute,
  type InputHTMLAttributes,
  type ReactNode,
  type Ref,
  useEffect,
} from "react";

import { cn } from "../../utils";
import { useTextFieldRootContext } from "./context";
import { inputContainerStyles, inputStyles } from "./style";

export type TextFieldInputProps = {
  className?: string;
  containerClassName?: string;
  prefix?: ReactNode;
  suffix?: ReactNode;
  disabled?: boolean;
  type?: Exclude<HTMLInputTypeAttribute, "number">;
  ref?: Ref<HTMLInputElement>;
  isWarning?: boolean;
} & Omit<InputHTMLAttributes<HTMLInputElement>, "prefix" | "suffix" | "type" | "autoCapitalize"> &
  Omit<AriaTextFieldOptions<"input">, "inputElementType" | "isDisabled" | "onChange" | "prefix" | "suffix"> &
  VariantProps<typeof inputStyles> &
  VariantProps<typeof inputContainerStyles>;

const Input = ({
  ref: forwardedRef,
  className,
  containerClassName,
  prefix,
  suffix,
  disabled,
  readOnly,
  variant = "outline",
  color = "primary",
  isWarning,
  onChange,
  ...props
}: TextFieldInputProps) => {
  const ref = useObjectRef(forwardedRef);
  const { setTextField } = useTextFieldRootContext<"input">();

  const textField = useTextField(
    {
      ...props,
      isReadOnly: readOnly,
      isDisabled: disabled,
      inputElementType: "input",
      // https://github.com/adobe/react-spectrum/issues/1860#issuecomment-849833808
      onChange: (value: string) =>
        onChange && onChange({ target: { value, name: props.name } } as ChangeEvent<HTMLInputElement>),
      label: true,
    },
    ref
  );

  const { inputProps, isInvalid } = textField;

  useEffect(() => {
    setTextField(textField);
  }, [isInvalid]);

  const handleFocus = () => ref.current?.focus();

  useEffect(() => {
    // reset value
    if (props.value === undefined && ref.current) {
      ref.current.value = "";
    }
  }, [props.value]);

  return (
    <>
      <style>
        {`input::-webkit-outer-spin-button,
          input::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
          }

          input[type=number] {
            -moz-appearance: textfield;
          }`}
      </style>
      <div
        className={cn(inputContainerStyles({ variant, color }), containerClassName)}
        data-invalid={isInvalid}
        data-warning={isWarning}
        data-disabled={inputProps.disabled}
        role="button"
        tabIndex={-1}
        onKeyUp={() => null}
        onClick={handleFocus}
      >
        {prefix}

        <input
          {...inputProps}
          className={cn(inputStyles({ variant }), className)}
          ref={ref}
          style={{ ...props.style }}
        />

        {suffix}
      </div>
    </>
  );
};

Input.displayName = "Input";

export default Input;
